import axiosInstance from 'shared/services/api/axiosInstance';

/**
 * Client side of the lazy filter catalog (GET /agent-runs/property-catalog and
 * /agent-runs/property-values).
 *
 * One module-level store, shared by every filter picker and value editor, so
 * two components asking for the same thing cost one request:
 *
 *   - cached per team, so the second open of a picker is free;
 *   - identical in-flight requests are shared (deduped). The underlying HTTP
 *     request is cancelled only when EVERY caller waiting on it has aborted,
 *     so one component unmounting never starves another;
 *   - `activateTeam(id)` drops everything belonging to any other team and
 *     cancels its in-flight requests, so a team switch can never show (or
 *     keep) the previous team's properties.
 *
 * Nothing here is preloaded. Callers decide when to ask.
 */

export const PAGE_SIZE = 50;
/** A value dropdown paints its first 25 at once; typing searches the server for the rest. */
export const VALUE_PAGE_SIZE = 25;
const CATALOG_TTL_MS = 5 * 60 * 1000;
const VALUES_TTL_MS = 60 * 1000;
const MAX_ENTRIES = 600;

/**
 * The fixed columns, known at build time, so the picker can paint before any
 * request returns. The server's `group=core` response replaces it.
 * Keep in step with RUN_FIXED_COLUMNS / SPAN_FIXED_COLUMNS / USER_PREDEFINED_TYPES
 * in backend/controllers/agentRunReadController.js.
 */
const f = (name, type) => ({ name, type, source: 'fixed', sampleValues: [] });
export const STATIC_CORE = {
  run_properties: [
    f('agent_name', 'string'), f('status', 'string'), f('distinct_id', 'string'),
    f('conversation_id', 'string'), f('parent_run_id', 'string'),
    f('started_at', 'datetime'), f('ended_at', 'datetime'), f('duration_ms', 'number'),
    f('total_tokens_in', 'number'), f('total_tokens_out', 'number'), f('total_cost', 'number'),
    f('span_count', 'number'), f('tool_count', 'number'), f('error_count', 'number'),
    f('feedback_satisfaction', 'string'), f('feedback_rating', 'number'),
    f('rage_score', 'number'), f('satisfaction_score', 'number'),
    f('cluster_id', 'number'), f('issue_cluster_id', 'number'), f('website_id', 'number'),
  ],
  span_properties: [
    f('kind', 'string'), f('name', 'string'), f('status', 'string'),
    f('started_at', 'datetime'), f('ended_at', 'datetime'), f('duration_ms', 'number'),
    f('model', 'string'), f('provider', 'string'), f('tool_name', 'string'),
    f('input_tokens', 'number'), f('output_tokens', 'number'), f('cost', 'number'),
    f('temperature', 'number'), f('parent_span_id', 'string'),
  ],
  user_properties: [
    f('first_location_city', 'string'), f('first_location_country', 'string'),
    f('last_location_city', 'string'), f('last_location_country', 'string'),
    f('first_referrer', 'string'), f('last_referrer', 'string'),
    f('device_type', 'string'), f('browser_name', 'string'), f('os', 'string'),
    f('utm_source', 'string'), f('utm_medium', 'string'), f('utm_campaign', 'string'),
    f('utm_term', 'string'), f('utm_content', 'string'), f('utm_id', 'string'),
  ],
};

/* ------------------------------------------------------------------ */
/* Cache + in-flight sharing                                           */
/* ------------------------------------------------------------------ */
const cache = new Map();     // key -> { at, ttl, data }
const inflight = new Map();  // key -> { subs, ac, promise }
let activeTeam = null;

const part = (v) => (v == null ? '' : String(v));
/** Team is always the first segment of a key, which is what activateTeam purges on. */
const key = (team, ...rest) => [part(team), ...rest.map(part)].join('\u0001');
const teamOfKey = (k) => k.split('\u0001', 1)[0];

export function isAbortError(e) {
  return !!e && (e.name === 'AbortError' || e.name === 'CanceledError' || e.code === 'ERR_CANCELED');
}
const abortError = () => Object.assign(new Error('canceled'), { name: 'AbortError', code: 'ERR_CANCELED' });

function remember(k, data, ttl) {
  while (cache.size >= MAX_ENTRIES) cache.delete(cache.keys().next().value);
  cache.set(k, { at: Date.now(), ttl, data });
}
function recall(k) {
  const hit = cache.get(k);
  if (!hit) return undefined;
  if (Date.now() - hit.at > hit.ttl) { cache.delete(k); return undefined; }
  return hit.data;
}

/**
 * Cached + deduped + abortable fetch. `run(signal)` performs the request.
 * Failures are never cached.
 */
function shared(k, ttl, run, signal) {
  const hit = recall(k);
  if (hit !== undefined) return Promise.resolve(hit);
  if (signal?.aborted) return Promise.reject(abortError());

  let entry = inflight.get(k);
  if (!entry) {
    const ac = new AbortController();
    entry = { subs: 0, ac };
    entry.promise = run(ac.signal)
      .then((data) => { remember(k, data, ttl); return data; })
      .finally(() => { if (inflight.get(k) === entry) inflight.delete(k); });
    entry.promise.catch(() => {}); // a cancelled request with no listener left is not an error
    inflight.set(k, entry);
  }
  entry.subs += 1;
  const mine = entry;

  return new Promise((resolve, reject) => {
    let settled = false;
    const onAbort = () => {
      if (settled) return;
      settled = true;
      mine.subs -= 1;
      if (mine.subs <= 0) {
        mine.ac.abort();
        if (inflight.get(k) === mine) inflight.delete(k);
      }
      reject(abortError());
    };
    if (signal) signal.addEventListener('abort', onAbort, { once: true });
    mine.promise.then(
      (d) => { if (!settled) { settled = true; resolve(d); } },
      (e) => { if (!settled) { settled = true; reject(e); } },
    );
  });
}

/**
 * Tell the store which team is on screen. Everything cached or in flight for
 * any other team is dropped. Call on mount and whenever the team changes.
 */
export function activateTeam(teamId) {
  const next = part(teamId);
  if (next === activeTeam) return;
  activeTeam = next;
  for (const k of [...cache.keys()]) if (teamOfKey(k) !== next) cache.delete(k);
  for (const [k, entry] of [...inflight.entries()]) {
    if (teamOfKey(k) !== next) { entry.ac.abort(); inflight.delete(k); }
  }
}

/** Drop one team's entries (after its properties are known to have changed). */
export function invalidateTeam(teamId) {
  const t = part(teamId);
  for (const k of [...cache.keys()]) if (teamOfKey(k) === t) cache.delete(k);
}

/** Tests only. */
export function resetCatalogStore() {
  cache.clear();
  for (const e of inflight.values()) e.ac.abort();
  inflight.clear();
  activeTeam = null;
}

/* ------------------------------------------------------------------ */
/* Core (fixed) properties                                             */
/* ------------------------------------------------------------------ */

/** What is known right now without a request: the cached core, else the static one. */
export function peekCore(teamId) {
  return recall(key(teamId, 'core')) || STATIC_CORE;
}

export function loadCore(teamId, { signal } = {}) {
  if (!teamId) return Promise.resolve(STATIC_CORE);
  return shared(key(teamId, 'core'), CATALOG_TTL_MS, async (sig) => {
    const { data } = await axiosInstance.get('/agent-runs/property-catalog', {
      params: { team_id: teamId, group: 'core' }, signal: sig,
    });
    return {
      run_properties: data?.run_properties || STATIC_CORE.run_properties,
      span_properties: data?.span_properties || STATIC_CORE.span_properties,
      user_properties: data?.user_properties || STATIC_CORE.user_properties,
    };
  }, signal);
}

/* ------------------------------------------------------------------ */
/* Deferred groups: one page at a time, or a name search               */
/* ------------------------------------------------------------------ */

/** One page of one group. @returns {Promise<{properties: Array, has_more: boolean}>} */
export function loadGroupPage({ teamId, agentName, group, offset = 0, limit = PAGE_SIZE, signal }) {
  return shared(key(teamId, 'page', agentName, group, offset, limit), CATALOG_TTL_MS, async (sig) => {
    const params = { team_id: teamId, group, limit, offset };
    if (agentName) params.agent_name = agentName;
    const { data } = await axiosInstance.get('/agent-runs/property-catalog', { params, signal: sig });
    return { properties: data?.properties || [], has_more: !!data?.has_more };
  }, signal);
}

/** Server-side property-name search across groups (at most `limit` matches). */
export function searchGroups({ teamId, agentName, q, groups, limit = PAGE_SIZE, signal }) {
  const term = String(q || '').trim();
  if (!term) return Promise.resolve({ properties: [], has_more: false });
  const gs = (groups || []).join(',');
  return shared(key(teamId, 'search', agentName, gs, term.toLowerCase(), limit), CATALOG_TTL_MS, async (sig) => {
    const params = { team_id: teamId, q: term, limit };
    if (agentName) params.agent_name = agentName;
    if (gs) params.groups = gs;
    const { data } = await axiosInstance.get('/agent-runs/property-catalog', { params, signal: sig });
    return { properties: data?.properties || [], has_more: !!data?.has_more };
  }, signal);
}

/* ------------------------------------------------------------------ */
/* Values for one property                                             */
/* ------------------------------------------------------------------ */

/**
 * One page of distinct values. `q` is a server-side substring search. Cached for
 * a minute: long enough that reopening a dropdown is free, short enough that a
 * value that has just started appearing shows up.
 * @returns {Promise<string[]>}
 */
export function loadValues({ teamId, agentName, field, q = '', offset = 0, limit = VALUE_PAGE_SIZE, signal }) {
  if (!teamId || !field) return Promise.resolve([]);
  const term = String(q || '').trim();
  return shared(key(teamId, 'values', agentName, field, term, offset, limit), VALUES_TTL_MS, async (sig) => {
    const params = { team_id: teamId, field, limit };
    if (offset) params.offset = offset;
    if (agentName) params.agent_name = agentName;
    if (term) params.q = term;
    const { data } = await axiosInstance.get('/agent-runs/property-values', { params, signal: sig });
    return data?.values || [];
  }, signal);
}
